import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ChannelLinkPayload,
  CommunityAttachmentKind,
  CommunityAttachmentResponse,
  CommunityChannel,
  CommunityChannelDetailResponse,
  CommunityChannelLink,
  CommunityChannelMode,
  CommunityEmbedResponse,
  CommunityHiddenUsersResponse,
  CommunityLanguageCheckResponse,
  CommunityMentionableResponse,
  CommunityOverview,
  CommunityPdfImportResponse,
  CommunityPost,
  CommunityPostNode,
  CommunityReactionResponse,
  CommunityReportResponse,
  CommunityRoleBrief,
  CommunitySearchResponse,
  CommunitySearchType,
  CommunitySubscribeResponse,
  CommunityThread,
  CommunityThreadDetailResponse,
  CommunityThreadListResponse,
  CommunityTranslationBatchResponse,
  CommunityTranslationMode,
  CommunityTranslationPreferencesResponse,
  CommunityTranslationResponse,
  CommunityUserBrief,
  CommunityVersionsResponse
} from '../models/community.models';
import { CommunityBlockedUser, CommunityPublicProfile } from '../models/community-profile.models';

export interface ListThreadsOptions {
  /** Omit for the newest threads across every readable channel. */
  channelId?: number | null;
  /** Legacy-topic filter, only meaningful inside an archive channel. */
  topicId?: number | null;
  page?: number;
  perPage?: number;
  /** Language the channel/group names come back in. */
  uiLocale?: string;
  /** Q&A filter (`qa` channels only): true = solved, false = open, omit/null = both. */
  solved?: boolean | null;
}

export interface ChannelCreatePayload {
  group_id: number;
  name: string;
  description?: string;
  mode: CommunityChannelMode;
  read_only: boolean;
}

export interface ChannelUpdatePayload {
  name?: string;
  description?: string;
  mode?: CommunityChannelMode;
  read_only?: boolean;
  archived?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CommunityService {
  private readonly overviewUrl = '/community/overview';
  private readonly channelsUrl = '/community/channels';
  private readonly threadsUrl = '/community/threads';
  private readonly postsUrl = '/community/posts';
  private readonly optInUrl = '/community/opt_in';
  private readonly usersUrl = '/community/users';
  private readonly hiddenUsersUrl = '/community/hidden_users';
  private readonly linksUrl = '/community/links';
  private readonly reportsUrl = '/community/reports';
  private readonly blocksUrl = '/community/blocks';
  private readonly adminChannelsUrl = '/community/admin/channels';
  private readonly mentionableUrl = '/community/mentionable';
  private readonly attachmentsUrl = '/community/attachments';
  private readonly pdfImportUrl = '/community/pdf_import';
  private readonly embedUrl = '/community/embed';
  private readonly searchUrl = '/community/search';
  private readonly translationPreferencesUrl = '/community/translation_preferences';
  private readonly languageCheckUrl = '/community/language_check';

  constructor(private http: HttpClient) {}

  // --- Structure ------------------------------------------------------------

  getOverview(uiLocale?: string): Observable<CommunityOverview> {
    return this.http.get<CommunityOverview>(this.overviewUrl, { params: this.localeParams(uiLocale) });
  }

  getChannel(id: number, uiLocale?: string): Observable<CommunityChannelDetailResponse> {
    return this.http.get<CommunityChannelDetailResponse>(`${this.channelsUrl}/${id}`, {
      params: this.localeParams(uiLocale)
    });
  }

  // --- Threads --------------------------------------------------------------

  listThreads(options: ListThreadsOptions = {}): Observable<CommunityThreadListResponse> {
    let params = this.localeParams(options.uiLocale)
      .set('page', String(options.page ?? 1))
      .set('per_page', String(options.perPage ?? 20));
    if (options.channelId != null) params = params.set('channel_id', String(options.channelId));
    if (options.topicId != null) params = params.set('topic_id', String(options.topicId));
    if (options.solved != null) params = params.set('solved', options.solved ? '1' : '0');
    return this.http.get<CommunityThreadListResponse>(this.threadsUrl, { params });
  }

  getThread(id: number, uiLocale?: string): Observable<CommunityThreadDetailResponse> {
    return this.http.get<CommunityThreadDetailResponse>(`${this.threadsUrl}/${id}`, {
      params: this.localeParams(uiLocale)
    });
  }

  /**
   * `locale` is the source language the writer confirmed after the language check
   * (§7.3). It is only sent when set, so a caller that skips the check keeps the
   * old behaviour: the server falls back to the writer's UI language.
   */
  createThread(
    channelId: number,
    title: string,
    body: string,
    locale?: string
  ): Observable<{ thread: CommunityThread }> {
    return this.http.post<{ thread: CommunityThread }>(this.threadsUrl, {
      thread: { channel_id: channelId, title, body, ...(locale ? { locale } : {}) }
    });
  }

  updateThread(
    id: number,
    title: string,
    body: string,
    locale?: string
  ): Observable<{ thread: CommunityThread }> {
    return this.http.patch<{ thread: CommunityThread }>(`${this.threadsUrl}/${id}`, {
      thread: { title, body, ...(locale ? { locale } : {}) }
    });
  }

  deleteThread(id: number): Observable<{ thread: CommunityThread }> {
    return this.http.delete<{ thread: CommunityThread }>(`${this.threadsUrl}/${id}`);
  }

  moveThread(threadId: number, channelId: number): Observable<{ thread: CommunityThread }> {
    return this.http.post<{ thread: CommunityThread }>(`${this.threadsUrl}/${threadId}/move`, {
      channel_id: channelId
    });
  }

  pinThread(id: number, pin: boolean): Observable<{ thread: CommunityThread }> {
    return this.http.post<{ thread: CommunityThread }>(
      `${this.threadsUrl}/${id}/${pin ? 'pin' : 'unpin'}`,
      {}
    );
  }

  lockThread(id: number, lock: boolean): Observable<{ thread: CommunityThread }> {
    return this.http.post<{ thread: CommunityThread }>(
      `${this.threadsUrl}/${id}/${lock ? 'lock' : 'unlock'}`,
      {}
    );
  }

  hideThread(id: number, hide: boolean): Observable<{ thread: CommunityThread }> {
    return this.http.post<{ thread: CommunityThread }>(
      `${this.threadsUrl}/${id}/${hide ? 'hide' : 'unhide'}`,
      {}
    );
  }

  // --- Posts ----------------------------------------------------------------

  /** `locale` as in `createThread`; without it the post inherits the thread's language. */
  createPost(
    threadId: number,
    body: string,
    parentPostId?: number | null,
    locale?: string
  ): Observable<{ post: CommunityPost }> {
    return this.http.post<{ post: CommunityPost }>(`${this.threadsUrl}/${threadId}/posts`, {
      post: { body, parent_post_id: parentPostId ?? null, ...(locale ? { locale } : {}) }
    });
  }

  updatePost(id: number, body: string, locale?: string): Observable<{ post: CommunityPost }> {
    return this.http.patch<{ post: CommunityPost }>(`${this.postsUrl}/${id}`, {
      post: { body, ...(locale ? { locale } : {}) }
    });
  }

  deletePost(id: number): Observable<{ post: CommunityPost }> {
    return this.http.delete<{ post: CommunityPost }>(`${this.postsUrl}/${id}`);
  }

  hidePost(id: number, hide: boolean): Observable<{ post: CommunityPost }> {
    return this.http.post<{ post: CommunityPost }>(
      `${this.postsUrl}/${id}/${hide ? 'hide' : 'unhide'}`,
      {}
    );
  }

  // --- Reactions (toggle) ---------------------------------------------------

  /** Toggle a reaction on a thread; `reaction` must be in the channel's `reaction_set`. */
  reactToThread(threadId: number, reaction: string): Observable<CommunityReactionResponse> {
    return this.http.post<CommunityReactionResponse>(`${this.threadsUrl}/${threadId}/reactions`, { reaction });
  }

  reactToPost(postId: number, reaction: string): Observable<CommunityReactionResponse> {
    return this.http.post<CommunityReactionResponse>(`${this.postsUrl}/${postId}/reactions`, { reaction });
  }

  // --- Pinned channel links (right `manage_links`) --------------------------

  createLink(channelId: number, link: ChannelLinkPayload): Observable<{ link: CommunityChannelLink }> {
    return this.http.post<{ link: CommunityChannelLink }>(`${this.channelsUrl}/${channelId}/links`, { link });
  }

  updateLink(id: number, link: ChannelLinkPayload): Observable<{ link: CommunityChannelLink }> {
    return this.http.patch<{ link: CommunityChannelLink }>(`${this.linksUrl}/${id}`, { link });
  }

  deleteLink(id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.linksUrl}/${id}`);
  }

  // --- Self opt-in roles ----------------------------------------------------

  optIn(roleKey: string): Observable<{ ok: boolean; role: CommunityRoleBrief }> {
    return this.http.post<{ ok: boolean; role: CommunityRoleBrief }>(`${this.optInUrl}/${encodeURIComponent(roleKey)}`, {});
  }

  optOut(roleKey: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.optInUrl}/${encodeURIComponent(roleKey)}`);
  }

  // --- Reports (§22) ----------------------------------------------------------

  /**
   * Report a thread or post to the channel's moderators. A second report of the
   * same item by the same member while the first is still open comes back with
   * `existing: true` instead of a new row.
   */
  createReport(reportableType: 'CommunityThread' | 'CommunityPost', reportableId: number, reason: string): Observable<CommunityReportResponse> {
    return this.http.post<CommunityReportResponse>(this.reportsUrl, {
      report: { reportable_type: reportableType, reportable_id: reportableId, reason }
    });
  }

  // --- User muting (admin) --------------------------------------------------

  /**
   * Mute/unmute a user: hides all their forum content and blocks them writing.
   * Since Phase 2 this writes a `hide`/`unhide` row into the moderation
   * protocol, so the reason is mandatory (422 `reason_required` without one).
   */
  hideUser(userId: number, hide: boolean, reason: string): Observable<{ user: CommunityUserBrief }> {
    return this.http.post<{ user: CommunityUserBrief }>(
      `${this.usersUrl}/${userId}/${hide ? 'hide' : 'unhide'}`,
      { reason }
    );
  }

  // --- Public profiles & blocks (§6/§9) ----------------------------------------

  /** Another member's profile, filtered by their visibility choices; 403 `community_blocked` when either side blocked the other. */
  getMemberProfile(userId: number): Observable<CommunityPublicProfile> {
    return this.http.get<CommunityPublicProfile>(`${this.usersUrl}/${userId}`);
  }

  blockUser(userId: number): Observable<{ ok: boolean; is_blocked: boolean }> {
    return this.http.post<{ ok: boolean; is_blocked: boolean }>(`${this.usersUrl}/${userId}/block`, {});
  }

  unblockUser(userId: number): Observable<{ ok: boolean; is_blocked: boolean }> {
    return this.http.delete<{ ok: boolean; is_blocked: boolean }>(`${this.usersUrl}/${userId}/block`);
  }

  listBlocks(): Observable<{ data: CommunityBlockedUser[] }> {
    return this.http.get<{ data: CommunityBlockedUser[] }>(this.blocksUrl);
  }

  listHiddenUsers(): Observable<CommunityHiddenUsersResponse> {
    return this.http.get<CommunityHiddenUsersResponse>(this.hiddenUsersUrl);
  }

  // --- Channel administration (admin) ---------------------------------------

  createChannel(payload: ChannelCreatePayload, uiLocale?: string): Observable<{ channel: CommunityChannel }> {
    return this.http.post<{ channel: CommunityChannel }>(this.adminChannelsUrl, { channel: payload }, {
      params: this.localeParams(uiLocale)
    });
  }

  updateChannel(id: number, changes: ChannelUpdatePayload, uiLocale?: string): Observable<{ channel: CommunityChannel }> {
    return this.http.patch<{ channel: CommunityChannel }>(`${this.adminChannelsUrl}/${id}`, { channel: changes }, {
      params: this.localeParams(uiLocale)
    });
  }

  /**
   * Delete a channel. When it still holds threads the backend answers 422 unless
   * `moveTo` names another channel that takes them over.
   */
  deleteChannel(id: number, moveTo?: number | null): Observable<{ ok: boolean }> {
    let params = new HttpParams();
    if (moveTo != null) params = params.set('move_to', String(moveTo));
    return this.http.delete<{ ok: boolean }>(`${this.adminChannelsUrl}/${id}`, { params });
  }

  // --- Versions -------------------------------------------------------------

  getThreadVersions(id: number): Observable<CommunityVersionsResponse> {
    return this.http.get<CommunityVersionsResponse>(`${this.threadsUrl}/${id}/versions`);
  }

  getPostVersions(id: number): Observable<CommunityVersionsResponse> {
    return this.http.get<CommunityVersionsResponse>(`${this.postsUrl}/${id}/versions`);
  }

  // --- Q&A (§17) --------------------------------------------------------------

  /** Mark `postId` as the best answer of a `qa` thread. */
  solveThread(threadId: number, postId: number): Observable<{ thread: CommunityThread }> {
    return this.http.post<{ thread: CommunityThread }>(`${this.threadsUrl}/${threadId}/solve`, { post_id: postId });
  }

  unsolveThread(threadId: number): Observable<{ thread: CommunityThread }> {
    return this.http.delete<{ thread: CommunityThread }>(`${this.threadsUrl}/${threadId}/solve`);
  }

  // --- Mentions & channel subscriptions (§11/§12) ------------------------------

  mentionable(channelId: number, query = ''): Observable<CommunityMentionableResponse> {
    let params = new HttpParams().set('channel_id', String(channelId));
    if (query) params = params.set('q', query);
    return this.http.get<CommunityMentionableResponse>(this.mentionableUrl, { params });
  }

  subscribeChannel(channelId: number): Observable<CommunitySubscribeResponse> {
    return this.http.post<CommunitySubscribeResponse>(`${this.channelsUrl}/${channelId}/subscribe`, {});
  }

  unsubscribeChannel(channelId: number): Observable<CommunitySubscribeResponse> {
    return this.http.delete<CommunitySubscribeResponse>(`${this.channelsUrl}/${channelId}/subscribe`);
  }

  // --- Attachments & video embeds (§14) -----------------------------------------

  /** Uploads an image/file for the editor; the caller inserts `attachment.url` into the body. */
  createAttachment(channelId: number, kind: CommunityAttachmentKind, file: File): Observable<CommunityAttachmentResponse> {
    const form = new FormData();
    form.append('channel_id', String(channelId));
    form.append('kind', kind);
    form.append('file', file, file.name);
    return this.http.post<CommunityAttachmentResponse>(this.attachmentsUrl, form);
  }

  /**
   * Converts an uploaded PDF into editable HTML. The document is NOT stored — the
   * server converts it in a tempfile and answers with the markup, which the editor
   * inserts at the caret like any other paste.
   */
  importPdf(channelId: number, file: File): Observable<CommunityPdfImportResponse> {
    const form = new FormData();
    form.append('channel_id', String(channelId));
    form.append('file', file, file.name);
    return this.http.post<CommunityPdfImportResponse>(this.pdfImportUrl, form);
  }

  /** Resolves a pasted video page URL to the allowlisted embed iframe. */
  resolveEmbed(channelId: number, url: string): Observable<CommunityEmbedResponse> {
    return this.http.post<CommunityEmbedResponse>(this.embedUrl, { channel_id: channelId, url });
  }

  // --- Search (§13) -------------------------------------------------------------

  search(query: string, type: CommunitySearchType = 'all', page = 1, perPage = 20): Observable<CommunitySearchResponse> {
    const params = new HttpParams()
      .set('q', query)
      .set('type', type)
      .set('page', String(page))
      .set('per_page', String(perPage));
    return this.http.get<CommunitySearchResponse>(this.searchUrl, { params });
  }

  // --- Translation (§15, F10) ----------------------------------------------------

  /**
   * Asks what language a draft looks like, right before it is saved (§7.3). Read
   * only — nothing is stored and nothing is enqueued, so it is safe to call on
   * every submit. `locale` states what the record would be saved as; without it
   * the server derives that from `thread_id` (a reply) or the caller's UI language.
   */
  languageCheck(payload: {
    title?: string;
    body: string;
    thread_id?: number;
    channel_id?: number;
    locale?: string;
  }): Observable<CommunityLanguageCheckResponse> {
    return this.http.post<CommunityLanguageCheckResponse>(this.languageCheckUrl, payload);
  }

  translateThread(threadId: number, locale: string): Observable<CommunityTranslationResponse> {
    return this.http.post<CommunityTranslationResponse>(`${this.threadsUrl}/${threadId}/translate`, { locale });
  }

  translatePost(postId: number, locale: string): Observable<CommunityTranslationResponse> {
    return this.http.post<CommunityTranslationResponse>(`${this.postsUrl}/${postId}/translate`, { locale });
  }

  /**
   * The thread page's collective call: status for the thread and the given posts
   * in one request, which is also what the client polls with while anything is
   * pending. `retry` clears an existing failure marker and re-queues — only ever
   * set from an explicit click, never by the automatic pass.
   */
  translateBatch(
    threadId: number,
    locale: string,
    postIds: number[] = [],
    retry = false
  ): Observable<CommunityTranslationBatchResponse> {
    const body: { locale: string; post_ids?: number[]; retry?: boolean } = { locale };
    if (postIds.length) body.post_ids = postIds;
    if (retry) body.retry = true;
    return this.http.post<CommunityTranslationBatchResponse>(`${this.threadsUrl}/${threadId}/translations`, body);
  }

  getTranslationPreferences(): Observable<CommunityTranslationPreferencesResponse> {
    return this.http.get<CommunityTranslationPreferencesResponse>(this.translationPreferencesUrl);
  }

  /** `mode: 'ask'` deletes the row — the server answers with the whole matrix either way. */
  updateTranslationPreference(
    sourceLocale: string,
    mode: CommunityTranslationMode
  ): Observable<CommunityTranslationPreferencesResponse> {
    return this.http.put<CommunityTranslationPreferencesResponse>(this.translationPreferencesUrl, {
      source_locale: sourceLocale,
      mode
    });
  }

  /** Turn a flat list of posts (ordered by created_at asc) into a tree of nodes. */
  buildTree(posts: CommunityPost[]): CommunityPostNode[] {
    const byId = new Map<number, CommunityPostNode>();
    const roots: CommunityPostNode[] = [];

    posts.forEach(p => byId.set(p.id, { ...p, replies: [] }));
    posts.forEach(p => {
      const node = byId.get(p.id)!;
      if (p.parent_post_id && byId.has(p.parent_post_id)) {
        byId.get(p.parent_post_id)!.replies.push(node);
      } else {
        roots.push(node);
      }
    });
    return roots;
  }

  private localeParams(uiLocale?: string): HttpParams {
    let params = new HttpParams();
    if (uiLocale) params = params.set('ui_locale', uiLocale);
    return params;
  }
}
